import React from 'react'
import { 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons,
  IonButton,
  IonIcon
} from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { arrowBackOutline, homeOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import UserMenu from './UserMenu'
import LanguageSwitcher from './LanguageSwitcher'

interface PageHeaderProps {
  title: string
  showBackButton?: boolean
  showLanguageSwitcher?: boolean
  children?: React.ReactNode
}

const PageHeader: React.FC<PageHeaderProps> = ({ 
  title, 
  showBackButton = true, 
  showLanguageSwitcher = true,
  children 
}) => {
  const history = useHistory()
  const { i18n } = useTranslation()
  
  const isGerman = i18n.language === 'de'
  const isLongTitle = title.length > 15
  
  // Since we now use shorter German titles globally, no additional abbreviation needed
  const displayTitle = title
  
  // Dynamic styling based on language and title length
  const getTitleClasses = () => {
    const baseClasses = "font-heading font-semibold text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
    
    // Use shorter titles on mobile, so we can use normal sizing
    return `${baseClasses} text-sm sm:text-base md:text-lg lg:text-xl max-w-full`
  }

  const handleBackClick = () => {
    if (history.length > 1) {
      history.goBack()
    } else {
      history.push('/dashboard')
    }
  }

  return (
    <IonHeader>
      <IonToolbar className="bg-white shadow-sm">
        {/* Match landing page layout with proper spacing */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            
            {/* Left side: InsightFlow branding + Back button */}
            <div className="flex items-center space-x-3">
              <div className="desktop-only flex items-center">
                <div className="text-2xl font-heading font-bold text-primary-600">
                  InsightFlow
                </div>
              </div>
              {showBackButton && (
                <IonButton 
                  fill="clear" 
                  size="small"
                  onClick={handleBackClick}
                  title="Go back"
                  className="text-gray-600 hover:text-primary-600"
                >
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              )}
            </div>
            
            {/* Center: Page title */}
            <div className="flex-1 text-center px-2">
              <div 
                className={getTitleClasses()}
                title={isLongTitle ? title : undefined}
              >
                {displayTitle}
              </div>
            </div>
            
            {/* Right side: Language switcher + Controls + hamburger menu */}
            <div className="flex items-center space-x-2">
              {showLanguageSwitcher && (
                <LanguageSwitcher size="small" />
              )}
              {children}
              <UserMenu />
            </div>
            
          </div>
        </div>
      </IonToolbar>
    </IonHeader>
  )
}

export default PageHeader 